import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  Plus, 
  Save, 
  Package, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Camera,
  Trash2,
  QrCode,
  Thermometer,
  Calendar,
  Hash,
  Box
} from 'lucide-react';

import { API } from '@/config';

export default function ReceiptDetailPage() {
  const navigate = useNavigate();
  const { receipt_id } = useParams();
  
  const [receipt, setReceipt] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [currentLineItem, setCurrentLineItem] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    fetchData();
  }, [receipt_id]);

  const fetchData = async () => {
    try {
      const [receiptRes, itemsRes] = await Promise.all([
        axios.get(`${API}/warehouse/receipts/${receipt_id}`),
        axios.get(`${API}/items`)
      ]);
      setReceipt(receiptRes.data);
      setItems(itemsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleAddLineItem = () => {
    setCurrentLineItem({
      item_id: '',
      item_name: '',
      lp_number: '',
      quantity_received: 0,
      uom: 'lbs',
      lot_number: '',
      production_date: '',
      expiration_date: '',
      supplier_lot_reference: '',
      photos: [],
      temperature_reading: null,
      qa_result: 'accept',
      qa_notes: '',
      hold_reason: '',
      assigned_location_id: null
    });
    setShowAddItemModal(true);
  };

  const handleItemSelect = (e) => {
    const item = items.find(i => i.item_id === e.target.value);
    if (item) {
      setCurrentLineItem({
        ...currentLineItem,
        item_id: item.item_id,
        item_name: item.name,
        uom: item.unit
      });
    }
  };

  const handleGenerateLicensePlate = async (lineItem) => {
    try {
      const response = await axios.post(
        `${API}/warehouse/receipts/${receipt_id}/generate-lp`,
        {
          item_id: lineItem.item_id,
          item_name: lineItem.item_name,
          lot_number: lineItem.lot_number,
          quantity: lineItem.quantity_received,
          uom: lineItem.uom,
          expiration_date: lineItem.expiration_date,
          photos: lineItem.photos,
          temperature_reading: lineItem.temperature_reading
        }
      );
      
      // Update the line item with the generated LP number
      setCurrentLineItem({
        ...lineItem,
        lp_number: response.data.lp_number
      });
      
      return response.data.lp_number;
    } catch (error) {
      console.error('Error generating license plate:', error);
      alert('Failed to generate license plate');
      return null;
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPhoto(true);
    
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setCurrentLineItem({
          ...currentLineItem,
          photos: [...(currentLineItem.photos || []), base64String]
        });
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading photo:', error);
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = (index) => {
    setCurrentLineItem({
      ...currentLineItem,
      photos: currentLineItem.photos.filter((_, i) => i !== index)
    });
  };

  const handleSaveLineItem = async () => {
    try {
      // Validate required fields
      if (!currentLineItem.item_id || !currentLineItem.quantity_received || !currentLineItem.lot_number) {
        alert('Please fill in all required fields (Item, Quantity, Lot Number)');
        return;
      }

      setSaving(true);

      // Generate license plate if not already generated
      let lpNumber = currentLineItem.lp_number;
      if (!lpNumber) {
        lpNumber = await handleGenerateLicensePlate(currentLineItem);
        if (!lpNumber) {
          setSaving(false);
          return;
        }
      }

      // Update the receipt with the new line item
      const updatedLineItems = [
        ...(receipt.line_items || []),
        { ...currentLineItem, lp_number: lpNumber }
      ];

      await axios.put(`${API}/warehouse/receipts/${receipt_id}`, {
        ...receipt,
        line_items: updatedLineItems,
        updated_at: new Date().toISOString()
      });

      // Create hold record if QA result is hold or reject
      if (currentLineItem.qa_result === 'hold' || currentLineItem.qa_result === 'reject') {
        await axios.post(`${API}/warehouse/holds`, {
          lp_number: lpNumber,
          item_id: currentLineItem.item_id,
          item_name: currentLineItem.item_name,
          lot_number: currentLineItem.lot_number,
          quantity_held: currentLineItem.quantity_received,
          reason: currentLineItem.hold_reason || currentLineItem.qa_notes,
          held_by: 'Current User', // TODO: Get from auth context
          location_id: 'HOLD-001',
          status: currentLineItem.qa_result === 'reject' ? 'rejected' : 'on_hold',
          photos: currentLineItem.photos
        });
      }

      // Refresh receipt data
      await fetchData();
      setShowAddItemModal(false);
      setCurrentLineItem(null);
      setSaving(false);
    } catch (error) {
      console.error('Error saving line item:', error);
      alert('Failed to save line item');
      setSaving(false);
    }
  };

  const handleCompleteReceipt = async () => {
    if (!receipt.line_items || receipt.line_items.length === 0) {
      alert('Please add at least one line item before completing the receipt');
      return;
    }

    if (!window.confirm('Are you sure you want to complete this receipt? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      await axios.put(`${API}/warehouse/receipts/${receipt_id}/complete`);
      alert('Receipt completed successfully!');
      navigate('/warehouse/receipts');
    } catch (error) {
      console.error('Error completing receipt:', error);
      alert('Failed to complete receipt');
      setSaving(false);
    }
  };

  const getQABadge = (qaResult) => {
    const styles = {
      accept: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      hold: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: AlertTriangle },
      reject: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle }
    };
    const style = styles[qaResult] || styles.accept;
    const Icon = style.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${style.bg} ${style.text}`}>
        <Icon className="w-3 h-3" />
        {qaResult.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading receipt...</p>
        </div>
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600">Receipt not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="p-4">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate('/warehouse/receipts')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold">Receipt Processing</h1>
              <p className="text-sm text-gray-600">PO: {receipt.po_number || 'N/A'}</p>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500">Receipt ID</div>
              <div className="font-mono text-sm">{receipt.receipt_id.substring(0, 8)}...</div>
            </div>
          </div>
          <div className="text-xs text-gray-500">
            Date: {new Date(receipt.receipt_date).toLocaleString()}
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold text-lg">Received Items</h2>
          <span className="text-sm text-gray-600">
            {receipt.line_items?.length || 0} item(s)
          </span>
        </div>

        {receipt.line_items && receipt.line_items.length > 0 ? (
          <div className="space-y-3">
            {receipt.line_items.map((lineItem, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="font-semibold text-base">{lineItem.item_name}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      <div className="flex items-center gap-1">
                        <QrCode className="w-3 h-3" />
                        LP: {lineItem.lp_number}
                      </div>
                    </div>
                  </div>
                  {getQABadge(lineItem.qa_result)}
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm mt-3">
                  <div>
                    <span className="text-gray-500">Quantity:</span>{' '}
                    <span className="font-medium">{lineItem.quantity_received} {lineItem.uom}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Lot:</span>{' '}
                    <span className="font-medium">{lineItem.lot_number}</span>
                  </div>
                  {lineItem.expiration_date && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Expires:</span>{' '}
                      <span className="font-medium">{new Date(lineItem.expiration_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  {lineItem.temperature_reading && (
                    <div>
                      <span className="text-gray-500 flex items-center gap-1">
                        <Thermometer className="w-3 h-3" />
                        Temp:
                      </span>{' '}
                      <span className="font-medium">{lineItem.temperature_reading}°F</span>
                    </div>
                  )}
                </div>

                {lineItem.qa_notes && (
                  <div className="mt-3 p-2 bg-gray-50 rounded text-sm">
                    <div className="text-gray-600 font-medium mb-1">QA Notes:</div>
                    <div className="text-gray-700">{lineItem.qa_notes}</div>
                  </div>
                )}

                {lineItem.photos && lineItem.photos.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs text-gray-600 mb-2">Photos ({lineItem.photos.length})</div>
                    <div className="flex gap-2 overflow-x-auto">
                      {lineItem.photos.map((photo, photoIndex) => (
                        <img
                          key={photoIndex}
                          src={photo}
                          alt={`Photo ${photoIndex + 1}`}
                          className="w-20 h-20 object-cover rounded border"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
            <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 mb-2">No items received yet</p>
            <p className="text-sm text-gray-400">Add items as they arrive</p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 space-y-2">
        <button
          onClick={handleAddLineItem}
          disabled={receipt.status === 'completed'}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
        >
          <Plus className="w-5 h-5" />
          Add Received Item
        </button>
        {receipt.status !== 'completed' && (
          <button
            onClick={handleCompleteReceipt}
            disabled={!receipt.line_items || receipt.line_items.length === 0}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
          >
            <CheckCircle className="w-5 h-5" />
            Complete Receipt
          </button>
        )}
      </div>

      {/* Add Line Item Modal */}
      {showAddItemModal && currentLineItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl my-8">
            {/* Modal Header */}
            <div className="border-b p-4 sticky top-0 bg-white rounded-t-lg">
              <h2 className="text-xl font-bold">Add Received Item</h2>
              <p className="text-sm text-gray-600 mt-1">Enter item details and perform QA inspection</p>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Item Selection */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Item <span className="text-red-500">*</span>
                </label>
                <select
                  value={currentLineItem.item_id}
                  onChange={handleItemSelect}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select an item...</option>
                  {items.map((item) => (
                    <option key={item.item_id} value={item.item_id}>
                      {item.name} ({item.sku})
                    </option>
                  ))}
                </select>
              </div>

              {/* Quantity and UOM */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Quantity Received <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={currentLineItem.quantity_received}
                    onChange={(e) => setCurrentLineItem({ ...currentLineItem, quantity_received: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Unit</label>
                  <input
                    type="text"
                    value={currentLineItem.uom}
                    onChange={(e) => setCurrentLineItem({ ...currentLineItem, uom: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-gray-50"
                    readOnly
                  />
                </div>
              </div>

              {/* Lot Number */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Lot Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={currentLineItem.lot_number}
                  onChange={(e) => setCurrentLineItem({ ...currentLineItem, lot_number: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter lot number"
                  required
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Production Date</label>
                  <input
                    type="date"
                    value={currentLineItem.production_date}
                    onChange={(e) => setCurrentLineItem({ ...currentLineItem, production_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Expiration Date</label>
                  <input
                    type="date"
                    value={currentLineItem.expiration_date}
                    onChange={(e) => setCurrentLineItem({ ...currentLineItem, expiration_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Supplier Lot Reference */}
              <div>
                <label className="block text-sm font-medium mb-1">Supplier Lot Reference</label>
                <input
                  type="text"
                  value={currentLineItem.supplier_lot_reference}
                  onChange={(e) => setCurrentLineItem({ ...currentLineItem, supplier_lot_reference: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional supplier reference"
                />
              </div>

              {/* Temperature Reading */}
              <div>
                <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                  <Thermometer className="w-4 h-4" />
                  Temperature Reading (°F)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={currentLineItem.temperature_reading || ''}
                  onChange={(e) => setCurrentLineItem({ ...currentLineItem, temperature_reading: parseFloat(e.target.value) || null })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter temperature"
                />
              </div>

              {/* QA Inspection */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium mb-2">
                  QA Inspection Result <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setCurrentLineItem({ ...currentLineItem, qa_result: 'accept' })}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 transition-colors ${
                      currentLineItem.qa_result === 'accept'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <CheckCircle className="w-4 h-4" />
                    Accept
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentLineItem({ ...currentLineItem, qa_result: 'hold' })}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 transition-colors ${
                      currentLineItem.qa_result === 'hold'
                        ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <AlertTriangle className="w-4 h-4" />
                    Hold
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentLineItem({ ...currentLineItem, qa_result: 'reject' })}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 transition-colors ${
                      currentLineItem.qa_result === 'reject'
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <XCircle className="w-4 h-4" />
                    Reject
                  </button>
                </div>
              </div>

              {/* QA Notes */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  QA Notes
                  {(currentLineItem.qa_result === 'hold' || currentLineItem.qa_result === 'reject') && (
                    <span className="text-red-500"> *</span>
                  )}
                </label>
                <textarea
                  value={currentLineItem.qa_notes}
                  onChange={(e) => setCurrentLineItem({ ...currentLineItem, qa_notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Enter QA inspection notes..."
                  required={currentLineItem.qa_result === 'hold' || currentLineItem.qa_result === 'reject'}
                />
              </div>

              {/* Hold Reason (if hold or reject) */}
              {(currentLineItem.qa_result === 'hold' || currentLineItem.qa_result === 'reject') && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {currentLineItem.qa_result === 'hold' ? 'Hold Reason' : 'Reject Reason'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={currentLineItem.hold_reason}
                    onChange={(e) => setCurrentLineItem({ ...currentLineItem, hold_reason: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={`Enter ${currentLineItem.qa_result} reason`}
                    required
                  />
                </div>
              )}

              {/* Photo Upload */}
              <div>
                <label className="block text-sm font-medium mb-2 flex items-center gap-1">
                  <Camera className="w-4 h-4" />
                  Photos
                </label>
                <div className="space-y-2">
                  {currentLineItem.photos && currentLineItem.photos.length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {currentLineItem.photos.map((photo, index) => (
                        <div key={index} className="relative">
                          <img
                            src={photo}
                            alt={`Upload ${index + 1}`}
                            className="w-full h-24 object-cover rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemovePhoto(index)}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <Camera className="w-5 h-5 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {uploadingPhoto ? 'Uploading...' : 'Add Photo'}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      disabled={uploadingPhoto}
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t p-4 flex gap-3 sticky bottom-0 bg-white rounded-b-lg">
              <button
                onClick={() => {
                  setShowAddItemModal(false);
                  setCurrentLineItem(null);
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLineItem}
                disabled={saving || !currentLineItem.item_id || !currentLineItem.quantity_received || !currentLineItem.lot_number}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Item'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
